import { useState, useCallback } from 'react'
import type { DatabaseConnection } from '@/stores/connectionStore'
import { AgentProxyService } from '@/services/agentProxyService'
import { useToast } from '@/hooks/useToast'
import { useQueryClient } from '@tanstack/react-query'
import { useFormValidation } from '@/hooks/useFormValidation'
import {
  ValidatedInput,
  ValidationSummary,
  FormSection,
  FormActions
} from '@/components/common/ValidatedForm'

interface CreateSchemaViewProps {
  connection: DatabaseConnection
  database: string
  onSchemaCreated: () => void
}

export function CreateSchemaView({ connection, database, onSchemaCreated }: CreateSchemaViewProps) {
  const [formData, setFormData] = useState({
    name: '',
    authorization: ''
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { showToast } = useToast()
  const queryClient = useQueryClient()

  // Form validation setup
  const { 
    validateField,
    validateForm,
    clearValidation,
    errors,
    warnings
  } = useFormValidation(connection, [])

  const resetForm = useCallback(() => {
    setFormData({
      name: '',
      authorization: ''
    })
    clearValidation()
    setIsSubmitting(false)
  }, [clearValidation])

  const handleFieldChange = useCallback(async (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    
    if (field === 'name') {
      await validateField(field, value, {
        required: true,
        type: 'identifier',
        maxLength: 63
      })
    }
  }, [validateField])

  const handleSubmit = useCallback(async (e?: React.FormEvent) => {
    if (e) e.preventDefault()
    
    const isFormValid = await validateForm(
      { name: formData.name },
      { name: { required: true, type: 'identifier', maxLength: 63 } }
    )
    
    if (!isFormValid.isValid) return

    try {
      setIsSubmitting(true)
      await AgentProxyService.createSchema(connection.id, database, {
        schema_name: formData.name.trim(),
        authorization: formData.authorization.trim() || undefined
      })
      showToast('success', 'Schema created successfully')
      
      // Invalidate and refetch schemas cache to show the new schema immediately
      // This will automatically refresh the database tree without manual refresh
      void queryClient.invalidateQueries({
        queryKey: ['databases', 'schemas', connection.id, database]
      })
      
      onSchemaCreated()
    } catch (error) {
      console.error('Failed to create schema:', error)
      let errorMessage = 'Failed to create schema'
      
      // Handle specific error cases
      const message = error instanceof Error ? error.message : ''
      if (message.includes('already exists')) {
        errorMessage = 'A schema with this name already exists'
              } else if (message.includes('UNSUPPORTED_OPERATION')) {
          errorMessage = 'Schema creation is only supported for PostgreSQL databases'
                } else if (message) {
          errorMessage = message
        }
      
      showToast('error', errorMessage)
    } finally {
      setIsSubmitting(false)
    }
  }, [formData.name, formData.authorization, validateForm, connection.id, database, showToast, queryClient, onSchemaCreated])

  return (
    <div className="p-6 h-full overflow-y-auto">
      <div className="max-w-4xl">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Create Schema</h2>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Create a new schema in database "{database}"
          </p>
        </div>

        {/* Validation Summary */}
        <ValidationSummary errors={errors} warnings={warnings} />

        <form onSubmit={(e) => void handleSubmit(e)} className="space-y-6">
          <FormSection title="Schema Configuration">
            <ValidatedInput
              label="Schema Name"
              name="name"
              value={formData.name}
              onChange={useCallback((value: string) => void handleFieldChange('name', value), [handleFieldChange])}
              error={errors.name}
              placeholder="Enter schema name"
              required
              disabled={isSubmitting}
              helpText="Must start with a letter or underscore and contain only letters, numbers, and underscores"
            />

            <ValidatedInput
              label="Authorization/Owner"
              name="authorization"
              value={formData.authorization}
              onChange={useCallback((value: string) => { setFormData(prev => ({ ...prev, authorization: value })) }, [])}
              placeholder="Owner username (optional)"
              disabled={isSubmitting}
              helpText="Leave empty to use the current user as owner"
            />
          </FormSection>

          <FormActions
            onSubmit={useCallback(() => void handleSubmit(), [handleSubmit])}
            onCancel={resetForm}
            submitLabel="Create Schema"
            isValid={!!formData.name.trim() && Object.keys(errors).length === 0}
            isSubmitting={isSubmitting}
          />
        </form>

        {/* Schema Info */}
        <div className="mt-8 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md p-4">
          <h4 className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-2">
            PostgreSQL Schema Notes
          </h4>
          <ul className="text-xs text-blue-800 dark:text-blue-200 space-y-1">
            <li>• Schemas provide a way to organize database objects</li>
            <li>• Schema names must be unique within the database</li>
            <li>• The 'public' schema is the default schema for most operations</li>
            <li>• You need CREATE privilege on the database to create schemas</li>
            <li>• Schema creation is only available for PostgreSQL databases</li>
          </ul>
        </div>
      </div>
    </div>
  )
}
